import React, { Suspense } from 'react';
import { StarIcon } from 'lucide-react';
import { formatIcons } from './constants';
// Enhanced Book Hover Card component - with null safety
export const BookHoverCard = ({
  book,
  darkMode,
  mousePosition
}: {
  book: any;
  darkMode: any;
  mousePosition: any;
}) => {
  // Return null if book is undefined
  if (!book) return null;
  // Determine if we should show the card on the left side based on mouse position
  const showOnLeft = mousePosition.x > window.innerWidth / 2;
  const cardWidth = 280; // Width of the card in pixels
  // Generate content tags based on book genre and type
  const getContentTags = (book: any) => {
    const tags: string[] = [];
    // Add genre-specific tags
    if (book.genre?.includes('Fantasy')) {
      tags.push('Magic');
      tags.push('Adventure');
    } else if (book.genre?.includes('Romance')) {
      tags.push('Slow Burn');
      if (book.type === 'series') tags.push('Character Growth');
    } else if (book.genre?.includes('Mystery') || book.genre?.includes('Thriller')) {
      tags.push('Suspense');
      tags.push('Plot Twists');
    } else if (book.genre?.includes('Horror')) {
      tags.push('Dark');
      tags.push('Atmospheric');
    }
    // Add book type tags
    if (book.type === 'anthology') {
      tags.push('Short Stories');
    } else if (book.type === 'cowrite') {
      tags.push('Collaboration');
    }
    return tags.slice(0, 3); // Limit to 3 tags
  };
  const contentTags = getContentTags(book);
  return <div className={`fixed z-30 w-72 p-4 rounded-lg shadow-md border ${darkMode ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-300'}`} style={{
    top: Math.max(mousePosition.y - 100, 20),
    left: showOnLeft ? Math.max(mousePosition.x - cardWidth - 20, 20) // Left side with minimum 20px from edge
    : Math.min(mousePosition.x + 20, window.innerWidth - cardWidth - 20) // Right side with minimum 20px from edge
  }} data-id="element-17305" data-mp="2ppngyk7myzcb">
      <div className="flex gap-3" data-id="element-17306" data-mp="m0sg1o3iv5b7">
        <div className="w-20 h-28 flex-shrink-0 flex flex-col" data-id="element-17307" data-mp="lzr5dkqf4ku0">
          <div className="w-full h-full rounded bg-center bg-cover" style={{
          backgroundImage: `url(${book.coverImage})`
        }} data-id="element-17308" data-mp="qyasvpknu4oh" />

          {/* Rating displayed under cover image */}
          <div className="mt-1 flex items-center justify-center bg-black bg-opacity-70 rounded-full p-1 text-white text-xs" data-id="element-17309" data-mp="10wi4ru03dwjm">
            <StarIcon className="h-3 w-3 text-amber-500 mr-0.5 fill-current" data-id="element-17310" data-mp="88m2m9rr7cfg" />
            <span data-id="element-17311" data-mp="86i11yhbupic">{book.rating}</span>
          </div>
        </div>
        <div className="flex-1 overflow-hidden" data-id="element-17312" data-mp="d6xc2je4zkx5">
          <h3 className="font-bold text-base mb-1 line-clamp-2" data-id="element-17313" data-mp="hyfbh30ihdg0">
            {book.title}
          </h3>
          {book.series && <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`} data-id="element-17314" data-mp="3gp7blqkdy5ik">
              {book.series} #{book.seriesNumber}
            </p>}
          {book.type === 'cowrite' && <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`} data-id="element-17315" data-mp="22rl5xvyxv32w">
              with {book.coAuthor}
            </p>}
          <div className="flex flex-wrap gap-1 mt-2" data-id="element-17316" data-mp="1tqembe0ztc5f">
            {book.genre && <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`} data-id="element-17317" data-mp="1orjwvt8ispcc">
                {book.genre}
              </span>}
            {book.type && <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-blue-900 text-blue-100' : 'bg-blue-100 text-blue-800'}`} data-id="element-17318" data-mp="1b184dt80woko">
                {book.type.charAt(0).toUpperCase() + book.type.slice(1)}
              </span>}
          </div>
          {/* Content Tags */}
          {contentTags.length > 0 && <div className="mt-2 flex flex-wrap gap-1" data-id="element-17319" data-mp="w4pnpsh5lce6">
              {contentTags.map((tag, index) => <span key={index} className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-purple-800/60 text-white' : 'bg-purple-100 text-purple-700'}`} data-id="element-17320" data-mp="2w8a29i5cyqpa" data-mpi={index}>
                  {tag}
                </span>)}
            </div>}
          <div className="flex items-center mt-2" data-id="element-17321" data-mp="2rde1ntm22v0k">
            <span className={`text-xs flex items-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-17322" data-mp="2deo124012d7g">
              {formatIcons[book.format]}
              <span className="ml-1" data-id="element-17323" data-mp="23hrtc0hydv3y">{book.format}</span>
            </span>
          </div>
        </div>
      </div>
    </div>;
};